import fetchConfigurationGroupProducts from "@secureo/common/utils/commerceTools/graphql/queries/fetchConfigurationGroupProducts"
import fetchLockingSystemProductsBySku from "@secureo/common/utils/commerceTools/graphql/queries/fetchLockingSystemProductsBySku"
import fetchCylinderConfigurationVariant from "@secureo/common/utils/commerceTools/graphql/queries/fetchCylinderConfigurationVariant"
import getCylinderConfigurationVariantSku from "@secureo/common/reducers/productDetailPageReducer/utils/getCylinderConfigurationVariantSku"
import getVariantsByVariantId from "@secureo/common/reducers/productDetailPageReducer/utils/getVariantsByVariantId"
import getInitialCylinderConfigurations from "@secureo/common/reducers/productDetailPageReducer/utils/getInitialCylinderConfigurations"
import getProductConfigurationFromConfigurationProduct from "@secureo/common/reducers/productDetailPageReducer/utils/configurationGroups/getProductConfigurationFromConfigurationProduct"
import getLockingSystemComponentFromLockingSystemProduct from "./getLockingSystemComponentFromLockingSystemProduct"
import { getLockingSystemComponentWithUpdatedCylinderConfigurationVariant } from "../actions"
import getPricePortions from "@secureo/common/reducers/productDetailPageReducer/utils/getPricePortions"
import getMsrpPricePortions from "@secureo/common/reducers/productDetailPageReducer/utils/getMsrpPricePortions"

import {
	LockingSystemProductsBySku,
	LockingSystemComponentsByUuid,
} from "@secureo/common/typings/LockingSystem"
import { CountryCode } from "@secureo/common/typings/CountryCode"
import { LockingSystemComponentDraft } from "./getComponentDraftsByUuidFromTextLineItems"
import {
	ConfigurationGroupProduct,
	CylinderConfigurationVariant,
} from "@secureo/common/typings/Product"
import { MinimalConfigurationsByConfigurationGroup } from "./getShoppingListDraftFromLockingSystemReducerState"
import {
	ProductConfigurationsByConfigurationGroup,
	ProductConfigurationsBySku,
} from "@secureo/common/reducers/productDetailPageReducer"

const fetchCylinderConfigurationVariantsBySku = async (
	accessToken: string,
	locale: string,
	lockingSystemProductsBySku: LockingSystemProductsBySku,
	lockingSystemComponentDrafts: LockingSystemComponentDraft[],
) => {
	const cylinderConfigurationVariantSkus = lockingSystemComponentDrafts.map(
		(lockingSystemComponentDraft) => {
			const { masterVariantSku, selectedSystem, selectedOuterLength, selectedInnerLength } =
				lockingSystemComponentDraft
			const lockingSystemProduct = lockingSystemProductsBySku[masterVariantSku]

			const { productType, variants } = lockingSystemProduct

			if (productType !== "Zylinder") return null
			const variantsByVariantId = getVariantsByVariantId(variants)
			const masterVariant = variantsByVariantId[1]
			const masterVariantAttributes = masterVariant?.attributes ?? {}

			const { cylinderConfigurationType } =
				getInitialCylinderConfigurations(masterVariantAttributes)

			const cylinderConfigurationVariantSku = getCylinderConfigurationVariantSku(
				cylinderConfigurationType,
				masterVariant.attributes,
				variantsByVariantId,
				selectedSystem,
				selectedOuterLength,
				selectedInnerLength,
			)

			return cylinderConfigurationVariantSku
		},
	)
	const uniqueCylinderConfigurationVariantSkus = Array.from(
		new Set(cylinderConfigurationVariantSkus.filter((sku) => !!sku)),
	)

	const cylinderConfigurationVariants = await Promise.all(
		uniqueCylinderConfigurationVariantSkus.map((sku) =>
			fetchCylinderConfigurationVariant(accessToken, sku, locale),
		),
	)

	const cylinderConfigurationVariantsBySku = cylinderConfigurationVariants.reduce(
		(cylinderConfigurationVariantsBySku, cylinderConfigurationVariant) => {
			const { sku } = cylinderConfigurationVariant

			return {
				...cylinderConfigurationVariantsBySku,
				[sku]: cylinderConfigurationVariant,
			}
		},
		{} as { [sku: string]: CylinderConfigurationVariant },
	)

	return cylinderConfigurationVariantsBySku
}

const fetchLockingSystemProductsAndConfigurations = async (
	accessToken: string,
	locale: string,
	lockingSystemComponentDrafts: LockingSystemComponentDraft[],
) => {
	const uniqueLockingSystemProductSkus = Array.from(
		new Set(lockingSystemComponentDrafts.map(({ masterVariantSku }) => masterVariantSku)),
	)

	const configurationGroupProductSkus = lockingSystemComponentDrafts.reduce(
		(configurationGroupProductSkus, componentDraft) => {
			const { minimalConfigurationsByConfigurationGroup } = componentDraft

			const configurationSkus = Object.values(
				minimalConfigurationsByConfigurationGroup,
			).reduce((configurationSkus, configurationsBySku) => {
				const skus = Object.keys(configurationsBySku)
				return [...configurationSkus, ...skus]
			}, [] as string[])

			return [...configurationGroupProductSkus, ...configurationSkus]
		},
		[] as string[],
	)
	const uniqueConfigurationGroupProductSkus = Array.from(new Set(configurationGroupProductSkus))

	const [lockingSystemProductsBySku, configurationGroupProducts] = await Promise.all([
		fetchLockingSystemProductsBySku(accessToken, uniqueLockingSystemProductSkus, locale, "EUR"),
		fetchConfigurationGroupProducts(accessToken, uniqueConfigurationGroupProductSkus, "EUR"),
	])

	const configurationGroupProductsBySku = configurationGroupProducts.reduce(
		(configurationGroupProductsBySku, configurationGroupProduct) => {
			const { sku } = configurationGroupProduct
			return {
				...configurationGroupProductsBySku,
				[sku]: configurationGroupProduct,
			}
		},
		{} as {
			[sku: string]: ConfigurationGroupProduct
		},
	)

	return {
		lockingSystemProductsBySku,
		configurationGroupProductsBySku,
	}
}

const getConfigurationsByConfigurationGroup = (
	countryCode: CountryCode,
	minimalConfigurationsByConfigurationGroup: MinimalConfigurationsByConfigurationGroup,
	configurationGroupProductsBySku: { [sku: string]: ConfigurationGroupProduct },
) => {
	const configurationsByConfigurationGroup = Object.entries(
		minimalConfigurationsByConfigurationGroup,
	).reduce(
		(
			configurationsByConfigurationGroup,
			[configurationGroupName, minimalConfigurationsBySku],
		) => {
			const configurationsBySku = Object.entries(minimalConfigurationsBySku).reduce(
				(configurationsBySku, [sku, minimalConfiguration]) => {
					const { quantity } = minimalConfiguration
					const configurationProduct = configurationGroupProductsBySku?.[sku] ?? null

					const configuration = getProductConfigurationFromConfigurationProduct(
						configurationProduct,
						configurationGroupName,
						quantity,
						countryCode,
					)

					return {
						...configurationsBySku,
						[sku]: configuration,
					}
				},
				{} as ProductConfigurationsBySku,
			)

			return {
				...configurationsByConfigurationGroup,
				[configurationGroupName]: configurationsBySku,
			}
		},
		{} as ProductConfigurationsByConfigurationGroup,
	)

	return configurationsByConfigurationGroup
}

const getComponentsByUuidFromComponentDrafts = async (
	accessToken: string,
	locale: string,
	countryCode: CountryCode,
	taxRate: number,
	componentDraftsByUuid: { [uuid: string]: LockingSystemComponentDraft },
	lockingComponentsUuids: string[],
	keyComponentsUuids: string[],
) => {
	const lockingSystemComponentDrafts = Object.values(componentDraftsByUuid)
	const { lockingSystemProductsBySku, configurationGroupProductsBySku } =
		await fetchLockingSystemProductsAndConfigurations(
			accessToken,
			locale,
			lockingSystemComponentDrafts,
		)

	const cylinderConfigurationVariantsBySku = await fetchCylinderConfigurationVariantsBySku(
		accessToken,
		locale,
		lockingSystemProductsBySku,
		lockingSystemComponentDrafts,
	)

	const lockingComponents = lockingComponentsUuids.map((uuid) => {
		const {
			name,
			quantity,
			masterVariantSku,
			sku,
			selectedSystem,
			selectedOuterLength,
			selectedInnerLength,
			minimalConfigurationsByConfigurationGroup,
		} = componentDraftsByUuid[uuid]

		const lockingSystemProduct = lockingSystemProductsBySku?.[masterVariantSku] ?? null
		const lockingComponentWithInitialConfigurations =
			getLockingSystemComponentFromLockingSystemProduct(
				lockingSystemProduct,
				"lock",
				countryCode,
				taxRate,
				quantity,
				name,
				uuid,
			)

		// TODO: Validate
		const configurationsByConfigurationGroup = getConfigurationsByConfigurationGroup(
			countryCode,
			minimalConfigurationsByConfigurationGroup,
			configurationGroupProductsBySku,
		)

		const cylinderConfigurationVariant = cylinderConfigurationVariantsBySku?.[sku] ?? null

		if (masterVariantSku !== sku) {
			const lockingComponent =
				getLockingSystemComponentWithUpdatedCylinderConfigurationVariant(
					{
						...lockingComponentWithInitialConfigurations,
						selectedSystem,
						selectedOuterLength,
						selectedInnerLength,
						// TODO: Validate
						configurationsByConfigurationGroup,
					},
					cylinderConfigurationVariant,
					taxRate,
					countryCode,
				)

			return lockingComponent
		} else {
			const lockingComponentWithConfigurations = {
				...lockingComponentWithInitialConfigurations,
				configurationsByConfigurationGroup,
			}

			const pricePortions = getPricePortions(
				lockingComponentWithConfigurations.variantsByVariantId,
				lockingComponentWithConfigurations.variantId,
				taxRate,
				quantity,
				countryCode,
				configurationsByConfigurationGroup,
				cylinderConfigurationVariant,
			)

			const {
				singleNetMsrpPriceCents,
				singleGrossMsrpPriceCents,
				totalNetMsrpPriceCents,
				totalGrossMsrpPriceCents,
				msrpPricePortions,
			} = getMsrpPricePortions(
				quantity,
				taxRate,
				countryCode,
				lockingComponentWithConfigurations.attributes,
				configurationsByConfigurationGroup,
			)
			return {
				...lockingComponentWithConfigurations,
				...pricePortions,
				singleNetMsrpPriceCents,
				singleGrossMsrpPriceCents,
				totalNetMsrpPriceCents,
				totalGrossMsrpPriceCents,
				msrpPricePortions,
			}
		}
	})

	const keyComponents = keyComponentsUuids.map((uuid) => {
		const { name, quantity, masterVariantSku, minimalConfigurationsByConfigurationGroup } =
			componentDraftsByUuid[uuid]

		const lockingSystemProduct = lockingSystemProductsBySku?.[masterVariantSku] ?? null
		const keyComponentWithInitialConfigurations =
			getLockingSystemComponentFromLockingSystemProduct(
				lockingSystemProduct,
				"key",
				countryCode,
				taxRate,
				quantity,
				name,
				uuid,
			)

		// TODO: Validate
		const configurationsByConfigurationGroup = getConfigurationsByConfigurationGroup(
			countryCode,
			minimalConfigurationsByConfigurationGroup,
			configurationGroupProductsBySku,
		)

		const pricePortions = getPricePortions(
			keyComponentWithInitialConfigurations.variantsByVariantId,
			keyComponentWithInitialConfigurations.variantId,
			taxRate,
			quantity,
			countryCode,
			configurationsByConfigurationGroup,
			null,
		)

		const {
			singleNetMsrpPriceCents,
			singleGrossMsrpPriceCents,
			totalNetMsrpPriceCents,
			totalGrossMsrpPriceCents,
			msrpPricePortions,
		} = getMsrpPricePortions(
			quantity,
			taxRate,
			countryCode,
			keyComponentWithInitialConfigurations.attributes,
			configurationsByConfigurationGroup,
		)

		const keyComponent = {
			...keyComponentWithInitialConfigurations,
			configurationsByConfigurationGroup,
			...pricePortions,
			singleNetMsrpPriceCents,
			singleGrossMsrpPriceCents,
			totalNetMsrpPriceCents,
			totalGrossMsrpPriceCents,
			msrpPricePortions,
		}

		return keyComponent
	})

	const components = [...lockingComponents, ...keyComponents]

	const componentsByUuid: LockingSystemComponentsByUuid = components.reduce(
		(componentsByUuid, component) => {
			const { uuid } = component
			return {
				...componentsByUuid,
				[uuid]: component,
			}
		},
		{} as LockingSystemComponentsByUuid,
	)

	return componentsByUuid
}

export default getComponentsByUuidFromComponentDrafts
